import React, { useState } from 'react';
import { 
  User,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Edit3,
  Settings,
  FileText,
  Phone,
  Mail,
  Shield,
  Calendar as CalendarIcon,
  MapPin,
  MessageSquare,
  Building,
  Camera,
  Clock,
  DollarSign,
  Target
} from 'lucide-react';
import { MotorbikeIcon } from '../Upload Doc Invoice/Icons';

const FormFieldsSettings = ({ 
  settings,
  defaultFieldSettings,
  handleSettingChange,
  updateFieldSetting,
  currentCompany
}) => {
  
  // Custom field management state
  const [showAddCustomField, setShowAddCustomField] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [newCustomField, setNewCustomField] = useState({
    key: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    description: '',
    icon: 'FileText',
    validationPattern: '',
    maxLength: null,
    options: []
  });

  // Available field types for custom fields
  const customFieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select', label: 'Dropdown Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'url', label: 'URL' }
  ];

  // Available icons for custom fields
  const customFieldIcons = [
    { value: 'FileText', label: 'Document', component: FileText },
    { value: 'User', label: 'Person', component: User },
    { value: 'Phone', label: 'Phone', component: Phone },
    { value: 'Mail', label: 'Email', component: Mail },
    { value: 'Calendar', label: 'Calendar', component: CalendarIcon },
    { value: 'MapPin', label: 'Location', component: MapPin },
    { value: 'MessageSquare', label: 'Message', component: MessageSquare },
    { value: 'Shield', label: 'Security', component: Shield },
    { value: 'Building', label: 'Building', component: Building },
    { value: 'Camera', label: 'Camera', component: Camera },
    { value: 'Clock', label: 'Time', component: Clock },
    { value: 'DollarSign', label: 'Money', component: DollarSign }
  ];

  // Get icon component for field
  const getFieldIcon = (iconName) => {
    const icons = {
      User, Phone, Mail, Shield, Calendar: CalendarIcon, MapPin, Bike: MotorbikeIcon, 
      CheckCircle: Eye, MessageSquare, FileText, Building, Camera, Clock, DollarSign
    };
    const IconComponent = icons[iconName] || FileText;
    return IconComponent;
  };

  // Get visible form fields ordered
  const getVisibleFields = () => {
    const fieldSettings = settings.fieldSettings || defaultFieldSettings;
    return Object.entries(fieldSettings)
      .filter(([fieldKey, fieldConfig]) => fieldConfig.visible)
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  // Custom field management functions
  const addCustomField = () => {
    if (!newCustomField.key.trim() || !newCustomField.label.trim()) {
      alert('Please enter both field key and label');
      return;
    }

    // Check if field key already exists
    if (settings.fieldSettings[newCustomField.key]) {
      alert('Field key already exists. Please choose a unique key.');
      return;
    }

    // Validate field key format (alphanumeric and underscores only)
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(newCustomField.key)) {
      alert('Field key must start with a letter and contain only letters, numbers, and underscores.');
      return;
    }

    const maxOrder = Math.max(...Object.values(settings.fieldSettings).map(f => f.order || 0));
    
    const customField = {
      visible: true,
      required: newCustomField.required,
      label: newCustomField.label.trim(),
      order: maxOrder + 1,
      icon: newCustomField.icon,
      placeholder: newCustomField.placeholder.trim(),
      description: newCustomField.description.trim(),
      fieldType: newCustomField.type,
      isCustom: true,
      validationPattern: newCustomField.validationPattern.trim(),
      maxLength: newCustomField.maxLength,
      options: newCustomField.type === 'select' ? newCustomField.options.filter(opt => opt.trim()) : undefined
    };

    const updatedFieldSettings = {
      ...settings.fieldSettings,
      [newCustomField.key]: customField
    };
    
    handleSettingChange('fieldSettings', updatedFieldSettings);

    // Reset form
    setNewCustomField({
      key: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      description: '',
      icon: 'FileText',
      validationPattern: '',
      maxLength: null,
      options: []
    });
    setShowAddCustomField(false);
  };

  const removeCustomField = (fieldKey) => {
    if (!settings.fieldSettings[fieldKey]?.isCustom) {
      alert('Cannot delete built-in fields');
      return;
    }

    const updatedFieldSettings = { ...settings.fieldSettings };
    delete updatedFieldSettings[fieldKey];
    
    handleSettingChange('fieldSettings', updatedFieldSettings);
  };

  const editCustomField = (fieldKey, fieldConfig) => {
    if (!fieldConfig.isCustom) {
      return; // Don't allow editing built-in fields through this method
    }

    setEditingCustomField(fieldKey);
    setNewCustomField({
      key: fieldKey,
      label: fieldConfig.label,
      type: fieldConfig.fieldType || 'text',
      required: fieldConfig.required,
      placeholder: fieldConfig.placeholder || '',
      description: fieldConfig.description || '',
      icon: fieldConfig.icon,
      validationPattern: fieldConfig.validationPattern || '',
      maxLength: fieldConfig.maxLength || null,
      options: fieldConfig.options || []
    });
    setShowAddCustomField(true);
  };

  const updateCustomField = () => {
    if (!newCustomField.label.trim()) {
      alert('Please enter field label');
      return;
    }

    const updatedFieldSettings = {
      ...settings.fieldSettings,
      [editingCustomField]: {
        ...settings.fieldSettings[editingCustomField],
        label: newCustomField.label.trim(),
        fieldType: newCustomField.type,
        required: newCustomField.required,
        placeholder: newCustomField.placeholder.trim(),
        description: newCustomField.description.trim(),
        icon: newCustomField.icon,
        validationPattern: newCustomField.validationPattern.trim(),
        maxLength: newCustomField.maxLength,
        options: newCustomField.type === 'select' ? newCustomField.options.filter(opt => opt.trim()) : undefined
      }
    };
    
    handleSettingChange('fieldSettings', updatedFieldSettings);

    setEditingCustomField(null);
    setNewCustomField({
      key: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      description: '',
      icon: 'FileText',
      validationPattern: '',
      maxLength: null,
      options: []
    });
    setShowAddCustomField(false);
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-3">
              <User className="w-6 h-6 text-orange-500" />
              Form Field Configuration
              <div className="ml-auto flex items-center text-orange-400 text-xs">
                <Target size={12} className="mr-1" />
                <span>Complete Field Control</span>
              </div>
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              Configure which fields are visible and required in reservation forms
            </p>
          </div>
          <button
            onClick={() => setShowAddCustomField(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
          >
            <Plus size={16} />
            <span>Add Custom Field</span>
          </button>
        </div>
      </div>
      
      <div className="p-6 space-y-4">
        {/* Form Behavior Settings - Compact */}
        <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <Settings size={14} className="text-orange-400" />
            Form Behavior Settings
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {[
              { key: 'compactMode', label: 'Compact layout' },
              { key: 'groupRelatedFields', label: 'Group fields' },
              { key: 'showFieldDescriptions', label: 'Show descriptions' },
              { key: 'showFieldIcons', label: 'Show icons' },
              { key: 'enableInlineValidation', label: 'Inline validation' },
              { key: 'showCharacterCounts', label: 'Character counts' }
            ].map(setting => (
              <label key={setting.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.formBehavior?.[setting.key]}
                  onChange={(e) => handleSettingChange('formBehavior', {
                    ...settings.formBehavior,
                    [setting.key]: e.target.checked
                  })}
                  className="mr-2 text-orange-500 scale-75"
                />
                <span className="text-gray-300">{setting.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Add/Edit Custom Field Form - FIXED DROPDOWN OPTIONS TEXTAREA */}
        {showAddCustomField && (
          <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium">
                {editingCustomField ? 'Edit Custom Field' : 'Add New Custom Field'}
              </h4>
              <button
                onClick={() => {
                  setShowAddCustomField(false);
                  setEditingCustomField(null);
                  setNewCustomField({
                    key: '', label: '', type: 'text', required: false, placeholder: '',
                    description: '', icon: 'FileText', validationPattern: '', maxLength: null, options: []
                  });
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {!editingCustomField && (
                <div>
                  <input
                    type="text"
                    value={newCustomField.key}
                    onChange={(e) => setNewCustomField(prev => ({ ...prev, key: e.target.value }))}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                    placeholder="Field Key*"
                  />
                </div>
              )}
              
              <div>
                <input
                  type="text"
                  value={newCustomField.label}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                  placeholder="Field Label*"
                />
              </div>

              <div>
                <select
                  value={newCustomField.type}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                >
                  {customFieldTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <select
                  value={newCustomField.icon}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                >
                  {customFieldIcons.map(icon => (
                    <option key={icon.value} value={icon.value}>{icon.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <input
                  type="text"
                  value={newCustomField.placeholder}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, placeholder: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                  placeholder="Placeholder text"
                />
              </div>

              <div className="col-span-2">
                <input
                  type="text"
                  value={newCustomField.description}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm"
                  placeholder="Description"
                />
              </div>

              {/* ✅ FIXED: Dropdown Options Textarea - Now Larger, Resizable, and Enter Key Works */}
              {newCustomField.type === 'select' && (
                <div className="col-span-full">
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    Dropdown Options (one per line)
                  </label>
                  <textarea
                    value={newCustomField.options.join('\n')}
                    onChange={(e) => {
                      // Don't filter empty lines here - let user add blank lines
                      const options = e.target.value.split('\n');
                      setNewCustomField(prev => ({ 
                        ...prev, 
                        options: options
                      }));
                    }}
                    onKeyDown={(e) => {
                      // ✅ FIX: Allow Enter key to create new lines
                      if (e.key === 'Enter') {
                        e.stopPropagation(); // Prevent form from capturing the event
                      }
                    }}
                    className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded text-sm min-h-32 resize-y"
                    placeholder="Enter options (one per line):&#10;Option 1&#10;Option 2&#10;Option 3"
                    rows="6"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {newCustomField.options.filter(opt => opt.trim()).length} option{newCustomField.options.filter(opt => opt.trim()).length !== 1 ? 's' : ''} added
                  </div>
                </div>
              )}

              <div className="col-span-full flex items-center justify-between">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={newCustomField.required}
                    onChange={(e) => setNewCustomField(prev => ({ ...prev, required: e.target.checked }))}
                    className="mr-2 text-red-500 scale-75"
                  />
                  <span className="text-white">Required field</span>
                </label>

                <div className="flex gap-2">
                  <button
                    onClick={editingCustomField ? updateCustomField : addCustomField}
                    className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-1 rounded text-sm hover:from-orange-600 hover:to-red-700 transition-colors"
                  >
                    {editingCustomField ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Individual Field Configuration - Compact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(settings.fieldSettings || defaultFieldSettings)
            .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
            .map(([fieldKey, fieldConfig]) => {
              const IconComponent = getFieldIcon(fieldConfig.icon);
              return (
                <div key={fieldKey} className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="bg-orange-500/20 p-1 rounded">
                        <IconComponent size={12} className="text-orange-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-white font-medium text-sm flex items-center gap-1 truncate">
                          {fieldConfig.label}
                          {fieldConfig.isCustom && (
                            <span className="text-xs bg-purple-900/30 text-purple-400 px-1 py-0.5 rounded border border-purple-700 flex-shrink-0">
                              Custom
                            </span>
                          )}
                        </h4>
                        <p className="text-gray-400 text-xs truncate">
                          {fieldKey}{fieldConfig.fieldType && ` • ${fieldConfig.fieldType}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {fieldConfig.isCustom && (
                        <>
                          <button
                            onClick={() => editCustomField(fieldKey, fieldConfig)}
                            className="p-1 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded hover:from-orange-600 hover:to-red-700 transition-colors"
                            title="Edit"
                          >
                            <Edit3 size={10} />
                          </button>
                          <button
                            onClick={() => removeCustomField(fieldKey)}
                            className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={10} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => updateFieldSetting(fieldKey, 'visible', !fieldConfig.visible)}
                        className={`transition-colors ${
                          fieldConfig.visible ? 'text-green-400' : 'text-gray-500'
                        }`}
                        title={fieldConfig.visible ? 'Visible' : 'Hidden'}
                      >
                        {fieldConfig.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Compact field controls */}
                  {fieldConfig.visible && (
                    <div className="space-y-2 pt-2 border-t border-gray-600">
                      <div className="flex items-center justify-between text-sm">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={fieldConfig.required}
                            onChange={(e) => updateFieldSetting(fieldKey, 'required', e.target.checked)}
                            className="mr-2 text-red-500 scale-75"
                            disabled={['customerName', 'pickupDateTime', 'dropoffDateTime', 'motorbikeNumber'].includes(fieldKey)}
                          />
                          <span className="text-gray-300">Required</span>
                        </label>
                        
                        {fieldConfig.fieldType && (
                          <span className="text-xs text-gray-500">
                            {fieldConfig.fieldType}
                          </span>
                        )}
                      </div>

                      {/* Quick edit for built-in fields */}
                      {!fieldConfig.isCustom && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={fieldConfig.label}
                            onChange={(e) => updateFieldSetting(fieldKey, 'label', e.target.value)}
                            className="flex-1 p-1 text-xs bg-gray-700 border border-gray-600 text-white rounded"
                            placeholder="Label"
                          />
                          {fieldConfig.placeholder !== undefined && (
                            <input
                              type="text"
                              value={fieldConfig.placeholder}
                              onChange={(e) => updateFieldSetting(fieldKey, 'placeholder', e.target.value)}
                              className="flex-1 p-1 text-xs bg-gray-700 border border-gray-600 text-white rounded"
                              placeholder="Placeholder"
                            />
                          )}
                        </div>
                      )}

                      {/* Preview */}
                      <div className="bg-gray-800/50 rounded p-1 border border-gray-600">
                        <div className="text-xs text-gray-400">Preview:</div>
                        <div className="text-white text-xs font-medium">
                          <span className="text-green-400 flex items-center gap-1">
                            <IconComponent size={10} />
                            {fieldConfig.label} {fieldConfig.required && <span className="text-red-400">*</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default FormFieldsSettings;